/**
 * Shared content & data for the marketing site.
 * Centralised so a copy tweak in one place updates every page.
 */
import {
  BookOpen, FileText, ClipboardList, Sparkles, GraduationCap, Presentation,
  Users, Wallet, MessageCircle, BarChart3, ShieldCheck, Layers, Languages,
  Zap, Rocket, Brain, Target, Globe2, FlaskConical, ClipboardCheck,
  HeartHandshake, CalendarCheck, Building2,
} from 'lucide-react';

// ─── Brand ────────────────────────────────────────────
export const BRAND = {
  name: 'ActanonEdu',
  tagline: 'AI Tool for Teachers · Student Intelligence Platform',
  whatsappNumber: '918796869006',
  whatsappLink: (text = '') =>
    `https://wa.me/918796869006?text=${encodeURIComponent(text)}`,
  supportEmail: 'support@actanonedu.com',
  infoEmail: 'info@actanonedu.com',
  legalEmail: 'legal@actanonedu.com',
  founderName: 'Founder',
  copyrightYear: '2026',
  legalName: 'Actanon Technologies Private Limited',
  address: 'Udyog Vihar Phase 5, Sector 19, Gurugram, Haryana 122016, India',
};

// ─── Top-level navigation ─────────────────────────────
export const PRIMARY_NAV = [
  { label: 'Home', href: '/' },
  { label: 'AI Teaching Tools', href: '/ai-tools-for-teachers' },
  { label: 'BOSCO AI', href: '/#bosco-ai' },
  { label: 'For Schools', href: '/school-erp' },
  { label: 'Resources', href: '/resources' },
];

export const FOOTER_LINKS = {
  Product: [
    { label: 'Features', href: '/features' },
    { label: 'AI Tools for Teachers', href: '/ai-tools-for-teachers' },
    { label: 'School ERP', href: '/school-erp' },
    { label: 'Pricing', href: '/pricing' },
  ],
  Company: [
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
    { label: 'School Pilot Program', href: '/school-pilot-program' },
    { label: 'Blog', href: '/blog' },
  ],
  Resources: [
    { label: 'Resources', href: '/resources' },
    { label: 'Help Center', href: '/help-center' },
    { label: 'FAQ', href: '/faq' },
  ],
  Legal: [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms & Conditions', href: '/terms-and-conditions' },
    { label: 'Refund Policy', href: '/refund-policy' },
    { label: 'Content Takedown Policy', href: '/content-takedown-policy' },
  ],
};

// ─── AI Tools (used on /features and /ai-tools-for-teachers) ──
export const AI_TOOLS = [
  {
    id: 'lesson-plan',
    name: 'Lesson Plan Generator',
    icon: BookOpen,
    tone: 'indigo',
    summary:
      'Board-aligned lesson plans with learning objectives, materials list, minute-by-minute activities, differentiation strategies, homework and reflection.',
    bullets: [
      'CBSE · ICSE · IGCSE · IB · CIS · State Boards',
      'Activity-by-minute time budget that always sums correctly',
      'Day-wise planner for multi-day units',
      'Auto-localise to Hindi, Tamil, Bengali, Marathi, Punjabi, Telugu',
    ],
  },
  {
    id: 'worksheet',
    name: 'Worksheet Generator',
    icon: ClipboardList,
    tone: 'emerald',
    summary:
      'Printable worksheets that respect grade level, difficulty bands, and your specific chapter — with answer keys.',
    bullets: [
      'Difficulty mix you control (Easy/Medium/Hard ratios)',
      'Scientific notation rendered correctly in DOCX & PDF',
      'Auto answer key + marking scheme',
      'Picture prompts for primary classes',
    ],
  },
  {
    id: 'quiz',
    name: 'Quiz Generator',
    icon: Zap,
    tone: 'amber',
    summary:
      'Snap quizzes, formative checks, exit tickets — generated in seconds with MCQ, TF, Fill, Short Answer.',
    bullets: [
      'Bloom-aligned questions across cognitive levels',
      'Auto-distractors for MCQ that test misconceptions',
      'Exam pattern: CBSE Section A–E, IGCSE Paper 1/2, IB IA',
      'Reuse across classes with one click',
    ],
  },
  {
    id: 'question-paper',
    name: 'Question Paper Generator',
    icon: FileText,
    tone: 'rose',
    summary:
      'Board-pattern question papers with section-wise marks distribution, blueprint matching, and validated arithmetic.',
    bullets: [
      'Section marks always equal sum of items (math reconciler)',
      'Blueprint upload — paper matches your school template',
      'Watermarked PDF for free tier; clean PDF for Pro+',
      'Internal choice handling per board',
    ],
  },
  {
    id: 'assignment',
    name: 'Assignment Generator',
    icon: ClipboardCheck,
    tone: 'violet',
    summary:
      'Homework that\'s thoughtful — varied formats, scaffolded by difficulty, with model answers ready for review.',
    bullets: [
      'Project · Research · Practice · Application formats',
      'Configurable due date, difficulty level, max marks',
      'Auto due-date reminders to students (school plan)',
      'Plagiarism-resistant prompts',
    ],
  },
  {
    id: 'teaching-material',
    name: 'Teaching Material Generator',
    icon: Presentation,
    tone: 'cyan',
    summary:
      'PPTs, teaching flows, summaries — the full lesson kit aligned to one chapter with one click.',
    bullets: [
      'PowerPoint slides via Presenton / SlidesGPT',
      'Summary in 3 lengths: Brief / Standard / Detailed',
      'Teaching Flow: warm-up → instruction → closure',
      'Lesson Kit: bundle all 4 docs in one batch',
    ],
  },
];

// ─── School ERP modules ──────────────────────────────
export const ERP_MODULES = [
  {
    id: 'attendance',
    name: 'Attendance',
    icon: CalendarCheck,
    tone: 'emerald',
    points: ['Daily roll-call', 'Period-wise attendance', 'Parent notification on absence', 'Monthly attendance reports'],
  },
  {
    id: 'fees',
    name: 'Fees & Finance',
    icon: Wallet,
    tone: 'amber',
    points: ['Razorpay-integrated fee collection', 'Installments & late-fee rules', 'Auto receipts in PDF', 'Defaulter dashboards'],
  },
  {
    id: 'communication',
    name: 'Communication',
    icon: MessageCircle,
    tone: 'indigo',
    points: ['Class & school broadcasts', 'Parent–teacher messaging', 'WhatsApp + email delivery', 'Read receipts & escalation'],
  },
  {
    id: 'student-records',
    name: 'Student Records',
    icon: Users,
    tone: 'violet',
    points: ['Admissions pipeline', 'Transfer certificates', 'Photo ID generation', 'Lifecycle from enquiry to alumni'],
  },
  {
    id: 'academics',
    name: 'Academics',
    icon: GraduationCap,
    tone: 'rose',
    points: ['Timetable builder & substitutes', 'Marks entry & report cards', 'Co-curricular tracking', 'Class teacher dashboard'],
  },
  {
    id: 'administration',
    name: 'Administration',
    icon: Building2,
    tone: 'cyan',
    points: ['HR & payroll for staff', 'Leave management', 'Inventory & assets', 'Audit trail across modules'],
  },
];

// ─── Multilingual support ────────────────────────────
export const LANGUAGES = [
  { code: 'en', label: 'English',   sample: 'Photosynthesis is how plants make food.',           script: 'Latin' },
  { code: 'hi', label: 'हिन्दी',     sample: 'प्रकाश-संश्लेषण से पौधे अपना भोजन बनाते हैं।',           script: 'Devanagari' },
  { code: 'mr', label: 'मराठी',     sample: 'प्रकाशसंश्लेषणाने झाडे अन्न तयार करतात.',                script: 'Devanagari' },
  { code: 'ur', label: 'اُردُو',      sample: 'فوٹو سنتھیسز سے پودے اپنی خوراک بناتے ہیں۔',         script: 'Nastaliq', rtl: true },
  { code: 'pa', label: 'ਪੰਜਾਬੀ',     sample: 'ਪ੍ਰਕਾਸ਼-ਸੰਸਲੇਸ਼ਣ ਨਾਲ ਪੌਦੇ ਆਪਣਾ ਭੋਜਨ ਬਣਾਉਂਦੇ ਹਨ।',     script: 'Gurmukhi' },
  { code: 'bn', label: 'বাংলা',      sample: 'সালোকসংশ্লেষণে গাছ নিজেদের খাবার তৈরি করে।',        script: 'Bengali' },
  { code: 'ta', label: 'தமிழ்',     sample: 'ஒளிச்சேர்க்கையால் தாவரங்கள் தங்கள் உணவை உருவாக்குகின்றன.', script: 'Tamil' },
];

// Future languages — shown as "Coming through 2026" chips
export const FUTURE_LANGUAGES = [
  { code: 'te', label: 'తెలుగు',    script: 'Telugu' },
  { code: 'kn', label: 'ಕನ್ನಡ',     script: 'Kannada' },
  { code: 'ml', label: 'മലയാളം',   script: 'Malayalam' },
  { code: 'gu', label: 'ગુજરાતી',   script: 'Gujarati' },
  { code: 'or', label: 'ଓଡ଼ିଆ',      script: 'Odia' },
  { code: 'as', label: 'অসমীয়া',    script: 'Assamese' },
  { code: 'sa', label: 'संस्कृतम्',  script: 'Sanskrit' },
];

// ─── Social proof / stats ───────────────────────────
// `to` = the numeric value the counter animates to.
// `suffix` / `prefix` decorate the rendered number.
export const STATS = [
  { to: 50000, suffix: '+', label: 'Lessons generated' },
  { to: 32000, suffix: '+', label: 'Worksheets created' },
  { to: 21000, suffix: '+', label: 'Quizzes generated' },
  { to: 4800,  suffix: '+', label: 'Teachers served' },
  { to: 50,    suffix: '+', label: 'Schools in pilot' },
  { to: 7,     suffix: '',  label: 'Indian languages' },
];

// ─── Testimonials ────────────────────────────────────
// `kind`: 'teacher' | 'school' | 'pilot' — used for filtering.
export const TESTIMONIALS = [
  {
    kind: 'teacher',
    quote: 'Saturday nights used to mean question papers. Now they mean my family.',
    name: 'Sunita Verma',
    role: 'Class 9 Math Teacher, Delhi',
  },
  {
    kind: 'teacher',
    quote: 'It speaks Hindi the way my students actually speak it. That was the game changer.',
    name: 'Rakesh Mishra',
    role: 'Hindi Faculty, UP',
  },
  {
    kind: 'school',
    quote: 'The blueprint upload is what sold our principal. Papers match our school format perfectly.',
    name: 'Mohit Sharma',
    role: 'Academic Head, Jaipur',
  },
  {
    kind: 'school',
    quote: 'Our entire science department prep time dropped by 60% in the first month. We added two more grade-bands the next week.',
    name: 'Dr. Anjali Rao',
    role: 'Principal, ICSE School · Mumbai',
  },
  {
    kind: 'pilot',
    quote: 'I generated a full IGCSE Physics paper with mark scheme in 4 minutes. We used to take a week.',
    name: 'Anita Pillai',
    role: 'Cambridge IGCSE Coordinator, Bengaluru',
  },
  {
    kind: 'pilot',
    quote: 'The founder personally onboarded my pilot. That level of care is what made me convert on day 22.',
    name: 'Capt. Vikram Singh (Retd.)',
    role: 'Director, CBSE School · Pune',
  },
];

// ─── Feature comparison (vs traditional / vs others) ─
export const COMPARISON = {
  columns: ['Traditional Prep', 'Generic AI Tools', 'ActanonEdu'],
  rows: [
    { label: 'Board-aligned (CBSE/ICSE/IGCSE/IB)', values: [false, false, true] },
    { label: 'Multilingual (7 Indian languages)', values: [true, false, true] },
    { label: 'Validated arithmetic on assessments', values: [false, false, true] },
    { label: 'Built-in School ERP', values: [false, false, true] },
    { label: 'Scientific notation in DOCX/PDF', values: [true, false, true] },
    { label: 'Per-school blueprint matching', values: [false, false, true] },
    { label: 'Hours saved per week', values: ['0', '2–3', '15+'] },
    { label: 'Privacy: data stays in India', values: [true, false, true] },
  ],
};

// ─── Pricing tiers ──────────────────────────────────
export const PRICING_TIERS = [
  {
    id: 'free',
    name: 'Free',
    audience: 'Individual Teachers',
    price: { inr: 0, suffix: 'forever' },
    cta: { label: 'Start free', href: '/signup' },
    features: [
      '1 doc per batch · 5 generations / month',
      'No PPT generation',
      'Watermarked DOCX & PDF downloads',
      'All 6 curriculum boards',
      'English + Hindi UI',
      'Community support',
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    audience: 'Individual Teachers',
    price: { inr: 299, suffix: 'per month' },
    cta: { label: 'Start with Starter', href: '/signup?plan=starter' },
    features: [
      '2 docs per batch · 100 generations / month',
      'Clean DOCX / PDF exports (no watermark)',
      'All 6 curriculum boards',
      '3 languages (English, Hindi, +1 of choice)',
      'Email support · 48-hr response',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    audience: 'Individual Teachers',
    price: { inr: 499, suffix: 'per month' },
    highlight: true,
    cta: { label: 'Go Pro', href: '/signup?plan=pro' },
    features: [
      '5 docs per batch · unlimited generations',
      'Clean DOCX / PDF / PPTX exports',
      'Detailed Mode (research-enriched)',
      'Bosco AI™ teaching assistant',
      'Blueprint upload',
      'All 7 languages',
      'Priority email support',
    ],
  },
  {
    id: 'elite',
    name: 'Elite',
    audience: 'Individual Teachers',
    price: { inr: 999, suffix: 'per month' },
    cta: { label: 'Go Elite', href: '/signup?plan=elite' },
    features: [
      '10 docs per batch · unlimited generations',
      'BOSCO Capture™ (Beta) — exclusive to Elite',
      'All Pro features included',
      'Early access to new AI tools',
      'AI image & worksheet illustrations',
      'WhatsApp founder support line',
      'Priority queue · 2× faster generation',
      'Custom prompts & saved blueprints',
    ],
  },
  {
    id: 'school-pilot',
    name: 'School Pilot',
    audience: 'Schools',
    price: { custom: 'Free 30-day pilot' },
    cta: { label: 'Apply for Pilot', wa: 'Hi, I want to bring ActanonEdu to my school via the 30-day pilot program.' },
    features: [
      'Up to 50 teachers',
      'Full ERP modules (Attendance/Fees/Communication)',
      'Founder-led onboarding',
      'Dedicated success manager',
      'Custom blueprint per school',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    audience: 'School Chains & Districts',
    price: { custom: 'Custom' },
    cta: { label: 'Talk to Founder', wa: 'Hi, we represent a school chain / district interested in ActanonEdu Enterprise.' },
    features: [
      'Multi-school admin console',
      'API access & SSO',
      'Custom integrations',
      'On-premise option',
      'Hindi-language SLA',
    ],
    comingSoon: true,
  },
];

// ─── FAQ items ──────────────────────────────────────
export const FAQ_ITEMS = [
  {
    q: 'Is ActanonEdu safe for student data?',
    a: 'Yes. We host on Indian servers, never train on your student data, encrypt at rest and in transit, and provide a signed DPA for school plans. Student data exits the platform on your request.',
  },
  {
    q: 'Which boards and grades do you support?',
    a: 'CBSE, ICSE/ISC, IGCSE (Cambridge), IB (MYP/DP), CIS, and all major State Boards across grades 1–12.',
  },
  {
    q: 'Can it generate in Hindi / Tamil / Bengali?',
    a: 'Yes — 7 Indian languages today: English, Hindi, Tamil, Telugu, Bengali, Marathi, Punjabi. More launching through 2026.',
  },
  {
    q: 'How is this different from ChatGPT?',
    a: 'We are board-pattern aware, math-validated, multilingual for Indian classrooms, render scientific notation in DOCX/PDF, and ship with a full School ERP. A generic chatbot cannot replace your school management system.',
  },
  {
    q: 'What is the School Pilot Program?',
    a: 'A 30-day free pilot for up to 50 teachers in your school, with founder-led onboarding and a custom blueprint match. Apply via the Pilot page or WhatsApp.',
  },
  {
    q: 'Do you offer refunds?',
    a: 'Yes — see our Refund Policy. Pro plans are refundable within 7 days if you raise a ticket explaining the issue.',
  },
  {
    q: 'Can a school chain use this across campuses?',
    a: 'Yes — Enterprise plans are designed for chains and districts with a multi-school console and SSO. Talk to the founder for details.',
  },
];

// ─── Blog posts (skeleton — replace with CMS later) ─
export const BLOG_CATEGORIES = [
  { slug: 'ai-tools', label: 'AI Tools' },
  { slug: 'school-erp', label: 'School ERP' },
  { slug: 'lesson-plans', label: 'Lesson Plans' },
  { slug: 'worksheets', label: 'Worksheets' },
  { slug: 'multilingual', label: 'Multilingual Content' },
  { slug: 'productivity', label: 'Teacher Productivity' },
];

// ─── Blog posts ─────────────────────────────────────
// Slugs intentionally match SEO landing-page keywords for topical authority.
// The blog acts as the "long-form content" arm; SEO landing pages are
// "product positioning" pages. Internal-linked together.
export const BLOG_POSTS = [
  {
    slug: 'ai-tool-for-teachers',
    title: 'AI tool for teachers in 2026 — what actually works inside an Indian classroom',
    category: 'ai-tools',
    keywords: ['AI tool for teachers', 'AI teaching assistant', 'classroom AI'],
    date: '2026-02-08',
    readTime: '9 min',
    excerpt: 'We graded 8 popular AI tools against 12 real teacher tasks. Spoiler: nobody is great at everything — and that\'s the point.',
  },
  {
    slug: 'lesson-plan-generator',
    title: 'The anatomy of an AI lesson plan generator — and how to evaluate one',
    category: 'lesson-plans',
    keywords: ['lesson plan generator', 'AI lesson plan'],
    date: '2026-01-29',
    readTime: '7 min',
    excerpt: 'Not all lesson plan generators are equal. Here are the 6 signals that separate a real teaching tool from a clever autocomplete demo.',
  },
  {
    slug: 'worksheet-generator',
    title: 'Worksheet generator playbook — difficulty mix, answer keys, and printable layouts',
    category: 'worksheets',
    keywords: ['worksheet generator', 'printable worksheet', 'CBSE worksheet'],
    date: '2026-01-22',
    readTime: '6 min',
    excerpt: 'A pragmatic Easy / Medium / Hard framework you can apply to any chapter — with examples from Class 7 Math.',
  },
  {
    slug: 'question-paper-generator',
    title: 'Question paper generators that actually match your school blueprint',
    category: 'productivity',
    keywords: ['question paper generator', 'blueprint matching', 'CBSE question paper'],
    date: '2026-01-15',
    readTime: '8 min',
    excerpt: 'Blueprint upload, section-marks arithmetic, internal-choice rules — the underrated features that decide whether a generator survives faculty rollout.',
  },
  {
    slug: 'school-erp-software',
    title: 'The 47-point School ERP checklist every Indian principal should run before signing',
    category: 'school-erp',
    keywords: ['school ERP software', 'school management system', 'school ERP checklist'],
    date: '2026-01-08',
    readTime: '10 min',
    excerpt: 'Free downloadable PDF. Vendors love showing dashboards. We help you ask the questions that predict whether the ERP will get used.',
  },
  {
    slug: 'hindi-lesson-plan-generator',
    title: 'Hindi lesson plan generator — getting register, grade-band and Devanagari right',
    category: 'multilingual',
    keywords: ['Hindi lesson plan generator', 'CBSE Hindi'],
    date: '2026-01-02',
    readTime: '6 min',
    excerpt: 'When we showed Hindi output to a teacher in Lucknow, she said "this sounds like a textbook from Mumbai." Here\'s how we fixed register, idiom and grade-band tone.',
  },
  {
    slug: 'tamil-question-paper-generator',
    title: 'Tamil question paper generator — pattern, register, and board conventions',
    category: 'multilingual',
    keywords: ['Tamil question paper generator', 'Tamil Nadu Board'],
    date: '2025-12-26',
    readTime: '7 min',
    excerpt: 'A side-by-side dissection of a Class 10 Tamil paper — section structure, marks distribution, and where most generators stumble on register.',
  },
  {
    slug: 'multilingual-ai-for-teachers',
    title: 'Multilingual AI for Indian classrooms — how we tested 7 languages with real teachers',
    category: 'multilingual',
    keywords: ['multilingual AI', 'AI Indian teachers', 'regional language AI'],
    date: '2025-12-18',
    readTime: '8 min',
    excerpt: 'Translation is not localisation. A walk-through of how we built native generation in 7 Indian languages — and the linguistic decisions that surprised us.',
  },
];

// ─── Pilot program terms (already on existing landing) ─
export const PILOT_TERMS = [
  { title: 'Duration',                items: ['Pilot access is strictly limited to 30 days.', 'Starts from the onboarding/activation date.', 'Non-extendable unless approved.'] },
  { title: 'Enrollment',              items: ['Applications open till 30th June.', 'Duration remains 30 days regardless of signup date.'] },
  { title: 'Access',                  items: ['Teacher tools + limited ERP features.', 'Features may evolve during the pilot.'] },
  { title: 'Usage',                   items: ['Fair usage applies.', 'Abuse may lead to access restriction.'] },
  { title: 'Feedback',                items: ['Schools agree to provide feedback during the pilot.'] },
  { title: 'Data',                    items: ['Data is handled securely.', 'Used only in anonymized form for product insights.'] },
  { title: 'No Continuity Guarantee', items: ['Pilot does not guarantee free access after 30 days.'] },
  { title: 'Conversion',              items: ['Option to upgrade to a paid plan post-pilot.'] },
  { title: 'Termination',             items: ['Platform may revoke access if misuse is detected.'] },
  { title: 'Acceptance',              items: ['Participation in the pilot = agreement to these terms.'] },
];

// Re-export icons for use in pages that need them
export {
  BookOpen, FileText, ClipboardList, Sparkles, GraduationCap, Presentation,
  Users, Wallet, MessageCircle, BarChart3, ShieldCheck, Layers, Languages,
  Zap, Rocket, Brain, Target, Globe2, FlaskConical, ClipboardCheck,
  HeartHandshake, CalendarCheck, Building2,
};
